/**
 * Unified time utility — all formatting goes through here so the whole app
 * uses the same locale and the user's own browser timezone automatically.
 */

// Detect once at module load so we never drift mid-session
const BROWSER_LOCALE: string =
  (typeof navigator !== 'undefined' && navigator.language) || 'en-US';
const BROWSER_TZ: string =
  (typeof Intl !== 'undefined' &&
    Intl.DateTimeFormat().resolvedOptions().timeZone) ||
  'UTC';

function toDate(input: string | number | Date): Date {
  return input instanceof Date ? input : new Date(input);
}

/** Full date + time + timezone abbreviation, e.g. "Feb 9, 2026, 03:14:05 PM EST" */
export function formatDateTime(input: string | number | Date): string {
  try {
    return new Intl.DateTimeFormat(BROWSER_LOCALE, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short',
      timeZone: BROWSER_TZ,
    }).format(toDate(input));
  } catch {
    return new Date(input).toLocaleString();
  }
}

/** Date only, e.g. "Feb 09, 2026" */
export function formatDate(input: string | number | Date): string {
  try {
    return new Intl.DateTimeFormat(BROWSER_LOCALE, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      timeZone: BROWSER_TZ,
    }).format(toDate(input));
  } catch {
    return new Date(input).toLocaleDateString();
  }
}

/** Time only, e.g. "03:14:05 PM" */
export function formatTime(input: string | number | Date): string {
  try {
    return new Intl.DateTimeFormat(BROWSER_LOCALE, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: BROWSER_TZ,
    }).format(toDate(input));
  } catch {
    return new Date(input).toLocaleTimeString();
  }
}

/**
 * Human-readable relative time using Intl.RelativeTimeFormat.
 * e.g. "2 minutes ago", "in 5 seconds"
 */
export function formatRelative(input: string | number | Date): string {
  try {
    const d = toDate(input);
    const diffMs = d.getTime() - Date.now();
    const diffSec = Math.round(diffMs / 1000);
    const rtf = new Intl.RelativeTimeFormat(BROWSER_LOCALE, { numeric: 'auto' });

    const abs = Math.abs(diffSec);
    if (abs < 60) return rtf.format(diffSec, 'second');
    const diffMin = Math.round(diffSec / 60);
    if (Math.abs(diffMin) < 60) return rtf.format(diffMin, 'minute');
    const diffHr = Math.round(diffMin / 60);
    if (Math.abs(diffHr) < 24) return rtf.format(diffHr, 'hour');
    const diffDay = Math.round(diffHr / 24);
    return rtf.format(diffDay, 'day');
  } catch {
    return '';
  }
}

/** Compact relative time for tight spaces: "5s ago", "2m ago", "3h ago", "4d ago" */
export function formatRelativeCompact(input: string | number | Date): string {
  try {
    const d = toDate(input);
    const diffSec = Math.floor((Date.now() - d.getTime()) / 1000);
    if (diffSec < 0) return 'just now';
    if (diffSec < 60) return `${diffSec}s ago`;
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
    return `${Math.floor(diffSec / 86400)}d ago`;
  } catch {
    return '';
  }
}

/** Expose detected values so components can show the detected timezone in the UI */
export const detectedLocale = BROWSER_LOCALE;
export const detectedTimezone = BROWSER_TZ;
